import type { UserDetail } from "@/entities/user";
import type { UserRole } from "@/entities/user-role";

import { Collapse, Table, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { getOrganizationMainRoles } from "@/entities/user-role";
import { UserOrganizationHeader } from "@/modules/user/user-organization-header";
import { UserRoleRemoveButton } from "@/modules/user-role/user-role-remove-button";

const { Title } = Typography;

export type TabRolesAndPermissionsProps = {
  user?: UserDetail;
  loading?: boolean;
};

export const TabRolesAndPermissions = ({
  user,
  loading,
}: TabRolesAndPermissionsProps) => {
  const { t } = useTranslation();

  if (!user) return null;

  const userRoles = user.roles ?? [];
  const mainRoles = getOrganizationMainRoles({
    roles: userRoles,
    organizationId: user.organization_id,
  });
  const mainRole = mainRoles[0] ? mainRoles[0] : undefined;
  // remove main role from the list of roles
  const filteredRoles = userRoles.filter((role) => role.id !== mainRole?.id);

  const filterBySubjectType = function (
    roles: UserRole[],
    subjectType: string
  ) {
    const filteredRoles =
      loading || !roles
        ? []
        : Object.values(roles).filter(
            (role) => role.subject_type == subjectType
          );
    return filteredRoles;
  };

  const roleColumns = function (type: string) {
    return [
      {
        title: t("user:role"),
        dataIndex: "behavior",
        render: (behavior: string) => behavior.split("\\").slice(-1)[0],
        key: "role",
      },
      {
        title: t("global:" + type),
        dataIndex: ["subject", "name"],
        key: "name",
      },
      {
        title: "",
        dataIndex: "behavior",
        render: (_behavior: string, role: UserRole) => {
          return <UserRoleRemoveButton role={role} userId={user.id} />;
        },
        key: "button",
      },
    ];
  };

  const items = [
    {
      key: "organizations",
      label: t("global:organizationPlural"),
      children: (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filterBySubjectType(
            filteredRoles,
            "App\\Models\\Organization"
          )}
          columns={roleColumns("organization")}
        />
      ),
    },
    {
      key: "locations",
      label: t("global:locationPlural"),
      children: (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filterBySubjectType(
            filteredRoles,
            "App\\Models\\Location"
          )}
          columns={roleColumns("location")}
        />
      ),
    },
    {
      key: "assets",
      label: t("global:assetPlural"),
      children: (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filterBySubjectType(filteredRoles, "App\\Models\\Asset")}
          columns={roleColumns("asset")}
        />
      ),
    },
    {
      key: "interventions",
      label: t("global:interventionPlural"),
      children: (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filterBySubjectType(
            filteredRoles,
            "App\\Models\\Intervention"
          )}
          columns={roleColumns("intervention")}
        />
      ),
    },
  ];

  return (
    <Space orientation="vertical" style={{ width: "100%" }}>
      <UserOrganizationHeader
        mainRole={mainRole}
        user={user}
        loading={loading}
      />
      <Title level={4}>{t("user:otherPermissions")}</Title>
      <Collapse
        accordion
        defaultActiveKey="organizations"
        expandIconPlacement="end"
        items={items}
      />
    </Space>
  );
};
